import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import QRScanner from "../components/QRScanner";
import OrderModal from "../components/OrderModal";
import DemoModal from "../components/DemoModal";
import BrandLogo from "../components/shared/BrandLogo";
import EmergencyLocketFeatures from "../components/ui/feature-grid-enterprise-grade";
import HeroSection from "../components/homepage/HeroSection";
import HowItWorksSection from "../components/homepage/HowItWorksSection";
import FooterSection from "../components/homepage/FooterSection";
import { CTA_PRICE } from "../config/pricing";
import {
  Ambulance,
  Users,
  Baby,
  Pill,
  ChevronLeft,
  ChevronRight,
  Link as LinkIcon,
  QrCode,
  ShoppingBag,
  Eye,
  Menu,
  X,
  Smartphone,
} from "lucide-react";

const DityaTechnologiesHomePage = () => {
  const navigate = useNavigate();
  const scrollContainerRef = React.useRef(null);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentUseCaseIndex, setCurrentUseCaseIndex] = useState(0);

  const handleQRScan = (locketId) => {
    if (locketId) {
      const id = String(locketId).trim().toUpperCase();
      navigate(`/${id}`);
    }
  };

  const scrollToSection = (sectionId) => {
    setMobileMenuOpen(false);
    const container = scrollContainerRef.current;
    const doScroll = () => {
      const element = document.getElementById(sectionId);
      if (element && container) {
        const headerOffset = 135;
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const targetScroll = Math.max(0, container.scrollTop + (elementRect.top - containerRect.top) - headerOffset);
        const start = container.scrollTop;
        const distance = targetScroll - start;
        const duration = 700;
        const startTime = performance.now();
        const easeOutCubic = (t) => 1 - (1 - t) ** 3;
        const animate = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          container.scrollTop = start + distance * easeOutCubic(progress);
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      }
    };
    setTimeout(doScroll, 150);
  };

  // Simplified to 4 most impactful use cases
  const useCases = [
    {
      IconComponent: Ambulance,
      title: "Road Accidents",
      description:
        "You're unconscious after a bike or car accident. The paramedic scans your QR code and immediately knows your blood type is B+, you're allergic to penicillin, and your wife's number. That's the difference this makes.",
      stat: "Over 1.5 lakh road deaths in India every year",
      colors: "bg-red-50 border-red-200",
      iconBg: "bg-red-100",
      textColor: "text-red-700",
    },
    {
      IconComponent: Users,
      title: "Elderly Parents Living Alone",
      description:
        "Your father takes 4 medications daily and lives in another city. If he collapses at the market, the QR locket on his wrist tells anyone exactly who to call and what medicines he's on.",
      stat: "10 crore+ senior citizens in India",
      colors: "bg-amber-50 border-amber-200",
      iconBg: "bg-amber-100",
      textColor: "text-amber-700",
    },
    {
      IconComponent: Baby,
      title: "Your Child Gets Lost",
      description:
        "Your child wanders off at a crowded mela or mall. A stranger finds them, scans the QR code on their bag or bracelet, and calls you within seconds. No police station, no loudspeaker needed.",
      stat: "A parent's biggest fear, solved simply",
      colors: "bg-pink-50 border-pink-200",
      iconBg: "bg-pink-100",
      textColor: "text-pink-700",
    },
    {
      IconComponent: Pill,
      title: "Diabetes, Heart Disease & Allergies",
      description:
        "You're diabetic and faint at work. Without this QR code, colleagues might give you sugar water when you actually need insulin. With it, they know exactly what's wrong and who to call.",
      stat: "7.7 crore+ diabetics in India alone",
      colors: "bg-purple-50 border-purple-200",
      iconBg: "bg-purple-100",
      textColor: "text-purple-700",
    },
  ];

  const nextUseCase = () => setCurrentUseCaseIndex((prev) => (prev + 1) % useCases.length);
  const prevUseCase = () => setCurrentUseCaseIndex((prev) => (prev - 1 + useCases.length) % useCases.length);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentUseCaseIndex((prev) => (prev + 1) % useCases.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [useCases.length]);

  // Listen for order modal open event from feature grid
  React.useEffect(() => {
    const handleOpenOrderModal = () => setShowOrderModal(true);
    window.addEventListener("openOrderModal", handleOpenOrderModal);
    return () => window.removeEventListener("openOrderModal", handleOpenOrderModal);
  }, []);

  const currentUseCase = useCases[currentUseCaseIndex];

  return (
    <div
      ref={scrollContainerRef}
      className="h-screen overflow-y-auto overflow-x-hidden snap-y snap-proximity scroll-pt-[135px] bg-white font-sans"
    >
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex justify-between items-center py-2 md:py-3">
            {/* Logo - white background preserved, fits nav height */}
            <BrandLogo logoClassName="h-10 md:h-11" />

            {/* Desktop Navigation */}
            <nav className="hidden md:flex gap-6 items-center">
              {[
                { label: "Home", target: "home" },
                { label: "How It Works", target: "emergency-locket" },
                { label: "Coming Next", target: "future-products" },
              ].map((item) => (
                <button
                  key={item.target}
                  onClick={() => scrollToSection(item.target)}
                  className="text-slate-700 font-medium text-base hover:text-red-600 transition-colors bg-transparent border-none cursor-pointer"
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => setShowDemoModal(true)}
                className="text-slate-700 font-medium text-base hover:text-red-600 transition-colors bg-transparent border-none cursor-pointer"
              >
                See Demo
              </button>
              <button
                onClick={() => setShowOrderModal(true)}
                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-md"
              >
                <ShoppingBag size={18} />
                Order — {CTA_PRICE}
              </button>
              <button
                onClick={() => setShowQRScanner(true)}
                className="flex items-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-lg font-semibold hover:bg-slate-900 transition-colors shadow-md"
              >
                <QrCode size={18} />
                Scan / Login
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-slate-700 bg-transparent border-none cursor-pointer p-2"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200 space-y-3">
              {[
                { label: "Home", target: "home" },
                { label: "How It Works", target: "emergency-locket" },
                { label: "Coming Next", target: "future-products" },
              ].map((item) => (
                <button
                  key={item.target}
                  onClick={() => scrollToSection(item.target)}
                  className="block w-full text-left text-slate-700 font-medium hover:text-red-600 bg-transparent border-none cursor-pointer py-1"
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => {
                  setShowDemoModal(true);
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left text-slate-700 font-medium hover:text-red-600 bg-transparent border-none cursor-pointer py-1"
              >
                See Demo
              </button>
              <button
                onClick={() => {
                  setShowOrderModal(true);
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 bg-red-600 text-white px-4 py-3 rounded-lg font-semibold"
              >
                <ShoppingBag size={18} />
                Order Now — {CTA_PRICE}
              </button>
              <button
                onClick={() => {
                  setShowQRScanner(true);
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 bg-slate-800 text-white px-4 py-3 rounded-lg font-semibold"
              >
                <QrCode size={18} />
                Scan / Login
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <div id="home" className="snap-start">
        <HeroSection onScanQR={() => setShowQRScanner(true)} onGetQRCode={() => setShowOrderModal(true)} />
      </div>

      {/* How It Works Section */}
      <div id="emergency-locket" className="snap-start">
        <HowItWorksSection onGetQRCode={() => setShowOrderModal(true)} onViewDemo={() => setShowDemoModal(true)} />
      </div>

      {/* Features Section */}
      <div className="snap-start">
        <EmergencyLocketFeatures />
      </div>

      {/* Use Cases Section - Simplified Carousel */}
      <section id="use-cases" className="snap-start py-8 md:py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-6 md:mb-8">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-slate-900 mb-3">
              When Would You Actually Need This?
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Real situations where a simple QR code can save a life — maybe yours
            </p>
          </div>

          {/* Carousel */}
          <div className="relative max-w-4xl mx-auto">
            <div
              className={`${currentUseCase.colors} border-2 rounded-lg p-5 md:p-8 transition-colors duration-150`}
            >
              {/* Navigation Buttons */}
              <button
                onClick={prevUseCase}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-md hover:bg-white transition-colors duration-150 z-10"
                aria-label="Previous use case"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={nextUseCase}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-md hover:bg-white transition-colors duration-150 z-10"
                aria-label="Next use case"
              >
                <ChevronRight size={20} />
              </button>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div className="text-center md:text-left">
                  <div
                    className={`inline-flex items-center justify-center w-20 h-20 ${currentUseCase.iconBg} rounded-lg mb-4`}
                  >
                    <currentUseCase.IconComponent size={40} className={currentUseCase.textColor} />
                  </div>
                  <h3 className={`text-2xl md:text-3xl font-extrabold ${currentUseCase.textColor} mb-3`}>
                    {currentUseCase.title}
                  </h3>
                  <span
                    className={`inline-block ${currentUseCase.iconBg} px-3 py-1 rounded-full text-xs font-semibold ${currentUseCase.textColor}`}
                  >
                    {currentUseCase.stat}
                  </span>
                </div>
                <div>
                  <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-4">{currentUseCase.description}</p>
                  <button
                    onClick={() => setShowOrderModal(true)}
                    className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-md"
                  >
                    <ShoppingBag size={18} />
                    Protect Yourself — {CTA_PRICE}
                  </button>
                </div>
              </div>

              {/* Dots */}
              <div className="flex justify-center gap-2 mt-4">
                {useCases.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentUseCaseIndex(idx)}
                    className={`h-2.5 rounded-full transition-all duration-150 ${
                      idx === currentUseCaseIndex ? "w-8 bg-red-600" : "w-2.5 bg-white/70 hover:bg-white/90"
                    }`}
                    aria-label={`Go to use case ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* QR Scanner CTA */}
          <div className="mt-6 md:mt-8 max-w-3xl mx-auto bg-red-50 border-2 border-red-200 rounded-lg p-5 md:p-6 text-center">
            <Smartphone size={48} className="text-red-600 mx-auto mb-3" />
            <h3 className="text-xl md:text-2xl font-extrabold text-slate-900 mb-2">
              Already Have Your <span className="text-red-600">QR Code?</span>
            </h3>
            <p className="text-slate-600 text-base mb-5 max-w-lg mx-auto">
              Scan it to view or update your emergency profile. You can also log in to edit your details.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setShowQRScanner(true)}
                className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700 transition-colors shadow-md"
              >
                <QrCode size={18} />
                Scan / Login
              </button>
              <button
                onClick={() => setShowDemoModal(true)}
                className="inline-flex items-center justify-center gap-2 bg-white text-red-600 border-2 border-red-600 px-6 py-3 rounded-lg font-bold hover:bg-red-50 transition-colors"
              >
                <Eye size={18} />
                View Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Future Products Section */}
      <section id="future-products" className="snap-start py-8 md:py-10 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-6 md:mb-8">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-slate-900 mb-3">Coming Next</h2>
            <p className="text-base md:text-lg text-slate-600">We're building more ways to keep you and your family safe.</p>
          </div>
          <div className="flex justify-center">
            <div className="bg-white p-5 rounded-lg shadow-md border border-slate-200 max-w-sm w-full text-center">
              <LinkIcon size={48} className="text-slate-800 mx-auto mb-3" />
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Premium Metallic Locket</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                A durable, water-resistant metallic chain with your QR code engraved. Looks like jewellery, works like a
                life-saver. Perfect for elderly parents.
              </p>
              <span className="text-red-600 font-semibold text-lg">Launching Soon</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <FooterSection />

      {/* Floating Action Button */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-center gap-2">
        <button
          onClick={() => setShowQRScanner(true)}
          className="bg-red-600 text-white w-14 h-14 rounded-full shadow-md flex items-center justify-center hover:bg-red-700 transition-colors duration-150"
          aria-label="Scan / Login"
        >
          <QrCode size={28} />
        </button>
        <span className="text-xs font-semibold text-slate-700 bg-white px-2 py-1 rounded-full shadow-md">Scan / Login</span>
      </div>

      {/* Modals */}
      <QRScanner isOpen={showQRScanner} onScan={handleQRScan} onClose={() => setShowQRScanner(false)} />
      <OrderModal isOpen={showOrderModal} onClose={() => setShowOrderModal(false)} />
      <DemoModal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)} />
    </div>
  );
};

export default DityaTechnologiesHomePage;
